import { Controller } from "@hotwired/stimulus"
import { Dropdown } from "bootstrap"

// The phone navigation (TMS-1106). Below 768px the app rail is hidden and the topbar
// burger opens a bottom sheet of the same apps as a grid; the avatar opens a
// full-screen account panel (TMS-1135) instead of its desktop dropdown.
//
// One controller owns both, on <body>, for two reasons: the controls that open them
// live in the topbar, outside either panel's markup, and only one of them may ever be
// up — they would otherwise stack, and both would claim the body scroll lock.
//
// The breakpoint is checked in JS as well as in CSS: the burger carries both this and
// rail-toggle, and each has to stand down outside its own range or one tap would both
// hide the desktop rail and open the sheet.
// Swipe-to-dismiss thresholds. A flick counts even when it barely moved, so a quick
// short swipe closes the sheet the way it does on iOS; a slow drag has to travel a
// quarter of the sheet's height before it is read as a dismissal rather than a fidget.
const DRAG_START_THRESHOLD_PX = 6
const DISMISS_DISTANCE_RATIO = 0.25
const DISMISS_VELOCITY_PX_PER_MS = 0.5
// A flick only counts if the finger was still moving when it left. Past this, the
// member was holding the sheet steady and the last recorded speed is stale.
const VELOCITY_STALE_AFTER_MS = 100

export default class extends Controller {
  static targets = [
    "sheet", "overlay", "panel", "trigger",
    "userPanel", "avatar"
  ]

  static classes = ["open"]

  static values = {
    mobileQuery: { type: String, default: "(max-width: 767px)" }
  }

  connect() {
    // The element currently up, or null. Also the answer to "is anything open".
    this.openPanel = null
    this.openTrigger = null
    this.drag = null
    this._handleKeydown = this._handleKeydown.bind(this)
    document.addEventListener("keydown", this._handleKeydown)

    // Growing past the breakpoint hides the sheet in CSS but would leave this
    // controller believing it is open — and the body scroll-locked, with nothing
    // visible to explain why. Rotating a phone to landscape is enough to hit it.
    this._mediaQuery = window.matchMedia(this.mobileQueryValue)
    this._handleBreakpointChange = this._handleBreakpointChange.bind(this)
    this._mediaQuery.addEventListener("change", this._handleBreakpointChange)

    this._syncTrigger()
    this._syncAvatarToggle()
    this._syncAvatar()
  }

  disconnect() {
    // A panel can still be open here — a Turbo visit from one of its links is exactly
    // that — and the page it inerted outlives this controller.
    this._clearBackgroundInert()
    // Hand the avatar back to Bootstrap: this controller may go away (a full page
    // load does exactly that) while the attribute it removed is still missing.
    if (this.hasAvatarTarget) {
      this.avatarTarget.setAttribute("data-bs-toggle", "dropdown")
    }
    document.removeEventListener("keydown", this._handleKeydown)
    this._mediaQuery.removeEventListener("change", this._handleBreakpointChange)
    this._unlockScroll()
  }

  // The topbar burger.
  toggle(event) {
    if (!this.hasSheetTarget) return
    this._toggle(event, this.sheetTarget, this.hasTriggerTarget ? this.triggerTarget : null)
  }

  // The topbar avatar. Above the breakpoint this does nothing and Bootstrap's
  // dropdown runs as it always has.
  toggleUser(event) {
    if (!this.hasUserPanelTarget) return
    this._toggle(event, this.userPanelTarget, this.hasAvatarTarget ? this.avatarTarget : null)
  }

  open(element, trigger = null) {
    if (this.openPanel === element) return
    // Opening one closes the other, so the two can never stack. Only reachable in one
    // direction today — the panel leaves the burger exposed, the sheet's overlay
    // covers the avatar — but the invariant is the point, not the path.
    if (this.openPanel) this.close()

    // The 9-dot switcher is a second nav surface on the same topbar, and it closes
    // itself from a document-level listener that _toggle's stopPropagation never lets
    // run. It cannot be tapped while it is open — its own overlay is fixed inset:0
    // over the topbar — but it is still keyboard-reachable, so Enter on the burger
    // left both up: the switcher stranded under this sheet, and its body scroll lock
    // released by whichever of the two closed first.
    this._closeAppSwitcher()

    this.openPanel = element
    this.openTrigger = trigger
    element.classList.add(this.openClass)
    element.removeAttribute("inert")
    if (this._usesOverlay(element)) this.overlayTarget.classList.add(this.openClass)
    this._setBackgroundInert(element, trigger)
    this._lockScroll()
    this._syncTrigger()
    this._syncAvatar()
    // aria-modal tells a screen reader the rest of the page isn't there. inert on
    // everything else is what makes that true rather than merely claimed: it takes the
    // topbar and the page out of the tab order, so Tab off the last row in the panel
    // wraps within it instead of walking into controls the member has been told are
    // gone — and it needs no hand-rolled trap to do it.
    element.focus()
  }

  close({ restoreFocus = true } = {}) {
    const element = this.openPanel
    if (!element) return

    const trigger = this.openTrigger
    this.openPanel = null
    this.openTrigger = null

    element.classList.remove(this.openClass)
    // Before the focus restore below, not after: the trigger is in the background this
    // marked inert, and focusing an inert element is a no-op that fails silently.
    this._clearBackgroundInert()
    // Focus moves out before the panel goes inert. Focus is on the panel itself at
    // this point, and marking a focused element inert is itself a focus change — the
    // browser drops it to <body>. Moving it deliberately first means we decide where
    // it lands rather than racing that.
    if (trigger && restoreFocus) trigger.focus()
    // Kept out of the tab order once shut. The panel stays in the DOM through its
    // transition, so visibility alone would leave its links focusable.
    element.setAttribute("inert", "")
    if (this._usesOverlay(element)) this.overlayTarget.classList.remove(this.openClass)
    // Escape or an overlay tap can land mid-swipe. Without this the drag state and
    // the pointer capture survive, and the next pointermove drags the closing sheet
    // back into view.
    this._endDrag()
    this._clearDragStyles()
    this._unlockScroll()
    this._syncTrigger()
    // Written directly rather than through _syncAvatar, which only speaks for the
    // mobile range: a breakpoint change fires this after the query has already
    // flipped, and the avatar would be left announcing an open menu that is shut.
    if (this.hasAvatarTarget) this.avatarTarget.setAttribute("aria-expanded", "false")
  }

  // A tile was tapped: an app link, or one of the support buttons that opens a
  // Bootstrap modal. Either way the sheet has done its job and must get out of the
  // way — an external link (target=_blank) or a modal would otherwise leave it open
  // behind them, still holding the body scroll lock.
  //
  // Closing here runs before Bootstrap's own delegated handler, so it releases the
  // lock and Bootstrap then takes it for the modal, rather than the two racing.
  closeOnItem(event) {
    if (event.target.closest("a, button")) this.close()
  }

  // The grabber does double duty (see _mobile_nav.html.erb). A drag that travelled but
  // did not dismiss springs the sheet back and is still followed by a click — without
  // this the sheet would shut anyway, which is the opposite of what letting go at the
  // top means. A dismissing drag also leaves the flag set, but by then openPanel is
  // null and close() is already a no-op.
  closeFromGrabber() {
    if (this._grabberDragged) {
      this._grabberDragged = false
      return
    }
    this.close()
  }

  // Swipe down to dismiss.
  //
  // From the grabber the gesture always starts — that element exists to be dragged,
  // and mobile_nav.scss gives it touch-action:none on exactly that assumption. From
  // the tile list it starts only when the list is already scrolled to the top;
  // otherwise a downward swipe is the member scrolling, and stealing it would make
  // the sheet impossible to read.
  dragStart(event) {
    if (this.openPanel !== this.sheetTarget || !this.isMobile) return
    if (!event.isPrimary) return
    if (event.pointerType === "mouse" && event.button !== 0) return

    const fromGrabber = !!event.target.closest(".mobile-nav-grabber")
    if (!fromGrabber && this.hasPanelTarget && this.panelTarget.scrollTop > 0) return

    // Every tap on the tab begins here, so this is where a stale flag from a gesture
    // that never produced a click gets cleared.
    this._grabberDragged = false
    this.drag = {
      pointerId: event.pointerId,
      fromGrabber,
      startY: event.clientY,
      // The last move, not the first: release velocity has to be read from the end of
      // the gesture, or a long slow drag would average away the flick that ends it.
      lastY: event.clientY,
      lastAt: event.timeStamp,
      velocity: 0,
      offset: 0,
      active: false
    }
  }

  dragMove(event) {
    if (!this.drag || event.pointerId !== this.drag.pointerId) return

    const distance = event.clientY - this.drag.startY

    if (!this.drag.active) {
      // An upward move is a scroll starting, not a dismissal — let the list have it.
      if (distance <= -DRAG_START_THRESHOLD_PX) return this._endDrag()
      if (distance < DRAG_START_THRESHOLD_PX) return

      this.drag.active = true
      // Capture so the rest of the gesture keeps arriving here even once the finger
      // leaves the sheet — which it does, since the sheet is travelling away from it.
      //
      // Guarded: capturing a pointer that is no longer active throws, and the pointer
      // can end between this pointerdown and the move that crosses the threshold.
      // Losing capture only costs us events once the finger leaves the sheet; letting
      // it throw would take the whole gesture handler down with it.
      try {
        this.sheetTarget.setPointerCapture(event.pointerId)
      } catch {
        // Pointer already gone — carry on without capture.
      }
      this.sheetTarget.style.transition = "none"
    }

    const elapsed = event.timeStamp - this.drag.lastAt
    if (elapsed > 0) {
      this.drag.velocity = (event.clientY - this.drag.lastY) / elapsed
      this.drag.lastY = event.clientY
      this.drag.lastAt = event.timeStamp
    }

    this.drag.offset = Math.max(0, distance)
    this.sheetTarget.style.transform = `translateY(${this.drag.offset}px)`
    if (this.hasOverlayTarget) {
      const travelled = Math.min(this.drag.offset / this.sheetTarget.offsetHeight, 1)
      this.overlayTarget.style.opacity = String(1 - travelled * 0.6)
    }
  }

  dragEnd(event) {
    const drag = this.drag
    if (!drag || event.pointerId !== drag.pointerId) return

    this._endDrag()
    if (!drag.active) return

    const flicked =
      event.timeStamp - drag.lastAt < VELOCITY_STALE_AFTER_MS &&
      drag.velocity > DISMISS_VELOCITY_PX_PER_MS
    const dismiss =
      drag.offset > this.sheetTarget.offsetHeight * DISMISS_DISTANCE_RATIO || flicked

    // Only the drag that springs back has a click worth swallowing. A dismissing one
    // is already closing, so setting the flag there would rest on the trailing click
    // arriving to clear it — and whether it does is not specified: close() marks the
    // sheet inert synchronously, from a gesture that began before that. If it never
    // came, the flag outlived the gesture and ate the next Enter on the tab.
    //
    // drag.fromGrabber, not event.target: the gesture captures the pointer to the
    // sheet once it activates, so by pointerup the target is the sheet either way.
    if (drag.fromGrabber && !dismiss) this._grabberDragged = true

    // Clearing the inline transform hands the sheet back to CSS: it either springs to
    // translateY(0), or rides the class removal out to translateY(100%).
    this._clearDragStyles()
    if (dismiss) this.close()
  }

  // Fired when the browser takes the gesture over for its own scrolling. The sheet
  // springs back rather than being left mid-drag.
  dragCancel() {
    this._endDrag()
    this._clearDragStyles()
  }

  get isOpen() {
    return this.openPanel !== null
  }

  get isMobile() {
    return this._mediaQuery.matches
  }

  // Private

  // Shared by the burger and the avatar: same breakpoint gate, same need to keep the
  // click off the document. The burger's other controller (rail-toggle) has already
  // stood down here.
  //
  // The avatar's Bootstrap dropdown is NOT held off by the guards below — see
  // _syncAvatarToggle. Bootstrap registers its delegated document handler with
  // addEventListener(type, fn, isDelegated), and that third argument is useCapture,
  // so it runs on the way down and has already toggled the dropdown before this
  // element-level listener is reached. stopPropagation here is too late.
  _toggle(event, element, trigger) {
    if (!this.isMobile) {
      // Keyboard only. A div emits no synthetic click on Enter or Space, and
      // Bootstrap's keydown handler covers just the arrows and Escape — so without
      // this, the avatar announces role="button" and does nothing when a desktop
      // keyboard user presses the obvious key, a false affordance this branch
      // introduced. Clicks stay Bootstrap's: its data-api listener runs in the capture
      // phase, so toggling here as well would open the menu and immediately shut it.
      if (event.type === "keydown" &&
          this.hasUserPanelTarget &&
          element === this.userPanelTarget &&
          this.hasAvatarTarget) {
        event.preventDefault()
        Dropdown.getOrCreateInstance(this.avatarTarget).toggle()
      }
      return
    }

    // preventDefault covers the keyboard path too: Space on a role="button" div would
    // otherwise scroll the page. Enter on one does nothing by itself, which is why
    // both keys are wired to this action at all. stopPropagation keeps the click off
    // the document listeners that read one as "outside" — the app switcher's, for
    // instance.
    event.preventDefault()
    event.stopPropagation()

    if (this.openPanel === element) {
      this.close()
    } else {
      this.open(element, trigger)
    }
  }

  // Only the app sheet is dimmed. The account panel covers the viewport under the
  // topbar outright, so there is nothing left to dim (Figma node 1030:34072).
  _usesOverlay(element) {
    return this.hasOverlayTarget && element === this.sheetTarget
  }

  _handleKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  // Everything in the page except the chrome that stays visible above the panel.
  //
  // Both full-screen panels start below the topbar, and below the impersonation banner
  // when there is one, so those two strips are on screen the whole time a panel is up.
  // Inerting them would leave controls a member can see, reach for and get nothing
  // from — the burger and the avatar, which are how the panels are toggled shut, and
  // "Stop impersonating", which is a staff safety control. A dialog's own way out
  // staying live is the ordinary arrangement; the page behind, which these panels
  // really do cover, is what had to stop being reachable.
  //
  // Within that chrome there is a second cut. The 9-dot switcher and the notification
  // bell are in the topbar but are not a way out of anything — each is a surface of its
  // own, and left live it opens underneath an overlay-less panel: invisible there,
  // holding its own scroll lock or focus, and revealed still up once the panel closes.
  // That is the stranding _closeAppSwitcher fixes in the other direction.
  //
  // Stated as a rule rather than a list of the two, so the next control added to the
  // bar is covered by default: inside the chrome, anything bringing its own Stimulus
  // controller brings its own behaviour, and goes inert — except the two controls this
  // one owns. Both have to be spared, not just whichever opened the panel: the burger
  // stays live while the account panel is up, and an example asserts the sheet opens
  // from there.
  //
  // Elements already inert (the panels that aren't open) are skipped, so restoring
  // cannot un-inert them.
  _setBackgroundInert(panel, trigger) {
    const overlay = this.hasOverlayTarget ? this.overlayTarget : null
    const chrome = []
    const background = []

    Array.from(document.body.children).forEach((element) => {
      if (element.contains(panel) || element === overlay) return
      if ((trigger && element.contains(trigger)) ||
          element.classList.contains("impersonation-banner")) {
        chrome.push(element)
        return
      }
      background.push(element)
    })

    const ours = [
      this.hasTriggerTarget ? this.triggerTarget : null,
      this.hasAvatarTarget ? this.avatarTarget : null
    ].filter(Boolean)
    const rivals = chrome.flatMap((element) =>
      Array.from(element.querySelectorAll("[data-controller]")).filter(
        (candidate) => !ours.some((own) => candidate.contains(own))
      )
    )

    this._inertedBackground = [ ...background, ...rivals ].filter(
      (element) => !element.hasAttribute("inert")
    )
    this._inertedBackground.forEach((element) => element.setAttribute("inert", ""))
  }

  _clearBackgroundInert() {
    if (!this._inertedBackground) return
    this._inertedBackground.forEach((element) => element.removeAttribute("inert"))
    this._inertedBackground = null
  }

  // The switcher's close() is public; its isOpen is the flag its own toggle reads.
  // Queried rather than held as a target: it lives in the topbar, outside this
  // controller's element on every layout that renders both.
  _closeAppSwitcher() {
    document.querySelectorAll('[data-controller~="app-switcher"]').forEach((element) => {
      const controller =
        this.application.getControllerForElementAndIdentifier(element, "app-switcher")
      if (controller && controller.isOpen) controller.close()
    })
  }

  _lockScroll() {
    document.body.style.overflow = "hidden"
  }

  _unlockScroll() {
    document.body.style.overflow = ""
  }

  _handleBreakpointChange(event) {
    // restoreFocus:false — otherwise merely rotating a phone to landscape yanks focus
    // onto the avatar from wherever the member had it.
    if (!event.matches) this.close({ restoreFocus: false })
    // Shrinking past the breakpoint with the desktop dropdown open would strand it:
    // nothing hides .dropdown-menu at this width, and the next avatar tap stops
    // propagation before Bootstrap's clearMenus can run, so the stale menu would sit
    // behind the panel — hidden by z-index but still open to a screen reader.
    if (event.matches && this.hasAvatarTarget) {
      Dropdown.getInstance(this.avatarTarget)?.hide()
    }
    this._syncTrigger()
    this._syncAvatarToggle()
    this._syncAvatar()
  }

  // The burger is one control with two jobs (see _rail_toggle_button.html.erb), and
  // its ARIA has to describe whichever one is live. Rendered server-side for the
  // desktop job; corrected here the moment the phone menu is the thing it opens.
  _syncTrigger() {
    if (!this.hasTriggerTarget) return

    if (this.isMobile) {
      this.triggerTarget.setAttribute("aria-controls", "mobile-nav-sheet")
      // The sheet specifically, not "anything is open" — the account panel is opened
      // by the avatar and says so on the avatar.
      this.triggerTarget.setAttribute(
        "aria-expanded",
        String(this.openPanel === this.sheetTarget)
      )
    } else {
      this.triggerTarget.setAttribute("aria-controls", "app-rail")
      this.triggerTarget.setAttribute(
        "aria-expanded",
        String(!document.body.classList.contains("is-rail-hidden"))
      )
    }
  }

  // Bootstrap's dropdown is switched off by taking the attribute its delegated
  // selector matches on — '[data-bs-toggle="dropdown"]' — off the avatar for as long
  // as we are below the breakpoint, and putting it back above.
  //
  // This is the only thing that actually works. The handler is registered in the
  // capture phase (see _toggle), so it fires before any listener on the avatar
  // itself: preventing or stopping the event there left the dropdown opening a frame
  // ahead of the panel and staying open behind it, its header edge showing above.
  _syncAvatarToggle() {
    if (!this.hasAvatarTarget) return

    // hasUserPanelTarget, not just isMobile: the avatar renders for every signed-in
    // user, the panel only when rail_enabled?. The two coincide today, but Registry
    // records that condition having been narrower once — and if it narrows again,
    // taking the attribute away with no panel to replace it leaves a phone tap doing
    // nothing at all.
    if (this.isMobile && this.hasUserPanelTarget) {
      this.avatarTarget.removeAttribute("data-bs-toggle")
    } else {
      this.avatarTarget.setAttribute("data-bs-toggle", "dropdown")
    }
  }

  // Only on mobile. Above the breakpoint the avatar's aria-expanded belongs to
  // Bootstrap, and writing it from here would describe a panel that cannot open.
  _syncAvatar() {
    if (!this.hasAvatarTarget || !this.hasUserPanelTarget) return
    if (!this.isMobile) return

    this.avatarTarget.setAttribute(
      "aria-expanded",
      String(this.openPanel === this.userPanelTarget)
    )
  }

  _endDrag() {
    // Guarded rather than blind: releasing a pointer that was never captured, or was
    // already released by a pointercancel, throws InvalidPointerId.
    if (this.drag?.active && this.sheetTarget.hasPointerCapture(this.drag.pointerId)) {
      this.sheetTarget.releasePointerCapture(this.drag.pointerId)
    }
    this.drag = null
  }

  _clearDragStyles() {
    this.sheetTarget.style.transition = ""
    this.sheetTarget.style.transform = ""
    if (this.hasOverlayTarget) this.overlayTarget.style.opacity = ""
  }
}
